import { useState } from 'react';
import { Mail, Trash2, UserCheck, UserX } from 'lucide-react';
import api from '../../../../utils/api';
import { apiErrorMessage } from '../../../../utils/apiError';
import { PartnerEndpoints } from '../../../../utils/endpoints';
import { Constants } from '../../../../utils/constants';
import { formatLagos } from '../../../../utils/datetime';
import { formatCount } from '../../../../utils/money';
import { useServerTable } from '../../../../hooks/useServerTable';
import {
  Alert,
  Button,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  IconButton,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../../ui';
import InviteUserModal from '../../modals/InviteUserModal';
import ConfirmActionModal from '../../modals/ConfirmActionModal';

const UsersTab = ({ subMerchant, canUpdate, onReload }) => {
  const table = useServerTable(PartnerEndpoints.subMerchantUsers(subMerchant.id), {
    sort: 'first_name',
    filters: { status: '' },
    fallbackError: 'We could not load the users. Please try again.',
  });

  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  const handleInvite = async (payload) => {
    await api.post(PartnerEndpoints.subMerchantUsers(subMerchant.id), payload);
    setNotice(`An invitation email has been sent to ${payload.email}.`);
    table.setPage(0);
    table.refetch();
    onReload();
  };

  const changeStatus = async (user, status) => {
    await api.patch(PartnerEndpoints.subMerchantUserStatus(subMerchant.id, user.id), { status });
    setNotice(
      status === 'deactivated' ? `${user.full_name} has been deactivated.` : `${user.full_name} has been reactivated.`
    );
    table.refetch();
  };

  const handleActivate = async (user) => {
    setActionError('');
    setActivatingId(user.id);
    try {
      await changeStatus(user, 'active');
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not activate this user.'));
    } finally {
      setActivatingId(null);
    }
  };

  const handleResend = async () => {
    const { data } = await api.post(PartnerEndpoints.subMerchantUserResend(subMerchant.id, resendTarget.id), {});
    setNotice(data?.message || 'A new password has been emailed to the user.');
  };

  const handleDelete = async () => {
    await api.delete(PartnerEndpoints.subMerchantUser(subMerchant.id, deleteTarget.id));
    setNotice(`${deleteTarget.full_name} has been removed.`);
    table.refetch();
    onReload();
  };

  const columns = [
    { key: 'full_name', header: 'Name', sortKey: 'first_name', render: (user) => user.full_name },
    { key: 'email', header: 'Email' },
    { key: 'phone_number', header: 'Phone' },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (user) => <StatusPill status={user.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.teamMemberStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Added',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (user) => <span className="whitespace-nowrap">{formatLagos(user.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Added"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
  ];

  if (canUpdate) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (user) => (
        <div className="flex items-center justify-center gap-1">
          <IconButton
            icon={Mail}
            label="Resend invite"
            tooltip="Email a new password and sign them out"
            onClick={() => setResendTarget(user)}
          />
          {user.status === 'deactivated' ? (
            <IconButton
              icon={UserCheck}
              label="Activate"
              tooltip="Restore this user's access"
              variant="link-success"
              disabled={activatingId === user.id}
              onClick={() => handleActivate(user)}
            />
          ) : (
            <IconButton
              icon={UserX}
              label="Deactivate"
              tooltip="Revoke this user's access"
              variant="link-danger"
              onClick={() => setDeactivateTarget(user)}
            />
          )}
          <IconButton
            icon={Trash2}
            label="Delete"
            tooltip="Remove this user"
            variant="link-danger"
            onClick={() => setDeleteTarget(user)}
          />
        </div>
      ),
    });
  }

  return (
    <>
      <TableCard
        title="Users"
        summary={{ label: 'Total', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            <SearchInput value={table.search} onChange={table.setSearch} placeholder="Search name or email" />
            {canUpdate && <Button onClick={() => setInviteOpen(true)}>Invite a user</Button>}
          </>
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        {(notice || actionError) && (
          <div className="flex flex-col gap-3 p-4 pb-0">
            {notice && (
              <Alert variant="success" onDismiss={() => setNotice('')}>
                {notice}
              </Alert>
            )}
            {actionError && (
              <Alert variant="danger" onDismiss={() => setActionError('')}>
                {actionError}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(user) => user.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No users', message: 'No users match these filters.' }}
        />
      </TableCard>

      <InviteUserModal
        isOpen={inviteOpen}
        note={`We email them a generated password. They sign in to the partner dashboard and see only ${subMerchant.name}.`}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
      />

      <ConfirmActionModal
        isOpen={Boolean(resendTarget)}
        title="Resend invite and sign them out"
        message={
          resendTarget
            ? `This generates a new password, emails it to ${resendTarget.email} and signs them out everywhere. Their current password stops working.`
            : ''
        }
        confirmLabel="Resend and sign out"
        onClose={() => setResendTarget(null)}
        onConfirm={handleResend}
      />

      <ConfirmActionModal
        isOpen={Boolean(deactivateTarget)}
        title="Deactivate user"
        message={
          deactivateTarget
            ? `This signs ${deactivateTarget.full_name} out and blocks them from signing in. It is reversible.`
            : ''
        }
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => changeStatus(deactivateTarget, 'deactivated')}
      />

      <ConfirmActionModal
        isOpen={Boolean(deleteTarget)}
        title="Delete user"
        message={
          deleteTarget
            ? `This signs ${deleteTarget.full_name} out and removes them from ${subMerchant.name}. Invite them again to restore access.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default UsersTab;
